/**
 * useAssistant Hook
 *
 * Manages assistant chat state and tRPC interactions
 */

import { useState} from 'react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export function useAssistant() {
  const { t } = useTranslation();
  const [conversationId, setConversationId] = useState<string | null>(null);

  // Get conversation query
  const { data: conversation, refetch } = trpc.chat.getConversation.useQuery(
    { conversationId: conversationId! },
    { enabled: !!conversationId }
  );

  // Send message mutation
  const sendMessageMutation = trpc.chat.sendMessage.useMutation({
    onSuccess: (data) => {
      setConversationId(data.conversationId);
      refetch();
    },
    onError: (error) => {
      toast.error(t('hooks.assistant.error_al_enviar_mensaje'), {
        description: error.message,
      });
    },
  });

  // Confirm action mutation
  const confirmActionMutation = trpc.chat.confirmAction.useMutation({
    // OJO: los dos parámetros van sí o sí. react-query pasa (data, variables) por
    // posición, así que borrar `data` por estar sin usar deja a `variables`
    // leyendo el RESULTADO — y el toast decía "cancelada" siempre.
    onSuccess: (_data, variables) => {
      if (variables.confirmed) {
        toast.success(t('hooks.assistant.accion_confirmada_y_ejecutada'));
      } else {
        toast.info(t('hooks.assistant.accion_cancelada'));
      }
      refetch();
    },
    onError: (error) => {
      toast.error(t('hooks.assistant.error_al_confirmar_accion'), {
        description: error.message,
      });
    },
  });

  // Archive conversation mutation
  const archiveConversationMutation = trpc.chat.archiveConversation.useMutation({
    onSuccess: () => {
      toast.success(t('hooks.assistant.conversacion_archivada'));
      setConversationId(null);
    },
    onError: (error) => {
      toast.error(t('hooks.assistant.error_al_archivar_conversacion'), {
        description: error.message,
      });
    },
  });

  const sendMessage = async (message: string) => {
    await sendMessageMutation.mutateAsync({
      conversationId,
      message,
    });
  };

  const confirmAction = async (
    messageId: string,
    toolCallIndex: number,
    confirmed: boolean
  ) => {
    if (!conversationId) return;

    await confirmActionMutation.mutateAsync({
      conversationId,
      messageId,
      toolCallIndex,
      confirmed,
    });
  };

  const archiveConversation = async () => {
    if (!conversationId) return;
    await archiveConversationMutation.mutateAsync({ conversationId });
  };

  const startNewConversation = () => {
    setConversationId(null);
  };

  return {
    conversationId,
    messages: conversation?.messages || [],
    conversation,
    isLoading:
      sendMessageMutation.isPending ||
      confirmActionMutation.isPending,
    sendMessage,
    confirmAction,
    archiveConversation,
    startNewConversation,
  };
}
